import {
  renderHeroArtwork,
  renderHeroTitle,
} from './public-home';
import { escapeSignInText, renderSignInForm } from './sign-in-form';

interface InviteScreenOptions {
  email?: string;
  status?: 'available' | 'unavailable' | 'checking' | 'error';
  target?: 'club';
  title?: string;
  timezone?: string;
}

export function getInviteAuthRedirectUrl(origin: string, inviteToken: string): string {
  const redirectUrl = new URL('/', origin);
  redirectUrl.searchParams.set('invite', inviteToken);
  return redirectUrl.toString();
}

export function renderInviteScreen(options: InviteScreenOptions = {}): string {
  const signedIn = Boolean(options.email);
  const status = options.status ?? 'available';
  const available = status === 'available';
  const isClub = options.target === 'club';
  const title = available ? (isClub ? 'Join a Daily Club' : 'Join a shared workout')
    : status === 'unavailable' ? 'Link expired or already used'
    : status === 'checking' ? 'Checking invitation…' : 'Could not check this link';
  const actionMarkup = status === 'checking' ? '<p role="status">Checking your access to the workout…</p>'
    : status === 'error' ? '<p role="alert">Please try again.</p><button id="invite-retry-btn" class="btn" type="button">Try again</button>'
    : status === 'unavailable' ? (signedIn
      ? '<p class="card-copy">This link can no longer be used to join the workout.</p><button id="logout-btn" class="btn btn-secondary" type="button">Use a different account</button>'
      : `<p class="card-copy">Already a participant? Sign in to open your workout.</p>${renderSignInForm()}`)
    : signedIn
    ? `
      <p class="auth-note">Signed in as ${escapeSignInText(options.email ?? '')}</p>
      <p class="card-copy">Joining adds you and the inviter to each other’s friends lists.</p>
      <div class="auth-form">
        <button id="redeem-invite-btn" class="btn btn-large" type="button">${isClub ? 'Join Club' : 'Join Workout'}</button>
        <button id="logout-btn" class="btn btn-secondary" type="button">Use a different account</button>
      </div>
    `
    : `
      <p class="card-copy">Sign in first, then you can choose to join.</p>
      ${renderSignInForm()}
      <p class="auth-note">The invitation will be waiting after you sign in.</p>
    `;

  return `
    <main class="screen login-screen" data-screen="invite">
      <header class="hero card workout-hero invite-hero">
        <div class="hero-content hero-content-workout">
          <div class="workout-hero-brand">
            <button id="invite-home-btn" class="workout-back-link" type="button">
              <span aria-hidden="true">←</span>
              Back to Home
            </button>
            ${renderHeroTitle()}
          </div>
          <div class="workout-hero-summary">
            <p class="workout-hero-challenge">${isClub ? 'Daily Club' : 'Workout'} Invitation</p>
            <span class="workout-hero-status workout-hero-status-active">${available ? 'A friend wants you on their team.' : title}</span>
          </div>
          ${renderHeroArtwork()}
        </div>
      </header>

      <section class="auth-container card" aria-labelledby="invite-title">
        ${available ? '<p class="eyebrow">You’re Invited</p>' : ''}
        <h2 id="invite-title">${title}</h2>
        ${available && isClub ? `<p class="card-copy">${escapeSignInText(options.title ?? 'Daily Club')} · Resets at midnight (${escapeSignInText(options.timezone ?? '')}). Membership continues each day until you leave. If today is complete, you start tomorrow.</p>` : ''}
        ${available ? '<p class="card-copy">Team up, log your exercises, and help reach the shared goal before time runs out.</p>' : ''}
        ${actionMarkup}
      </section>
    </main>
  `;
}
